import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  FaShieldAlt, 
  FaCheck, 
  FaArrowRight, 
  FaUsers,
  FaCrown,
  FaInfinity,
  FaBuilding,
  FaSpinner,
  FaTimes,
  FaDownload
} from "react-icons/fa";
import Logo from "../components/Logo";
import Footer from "../components/Footer";
import { getApiUrl } from "../utils/apiConfig";
import { ACCESS_TOKEN } from "../constants";
import "../styles/Subscription.css";

function Subscription() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userSubscription, setUserSubscription] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [paymentError, setPaymentError] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState('');
  const [razorpayKeyId, setRazorpayKeyId] = useState('');

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem(ACCESS_TOKEN);
    if (token) {
      setIsLoggedIn(true);
      fetchSubscriptionStatus();
      fetchPlans();
    }
    
    // Load Razorpay script
    loadRazorpayScript();
  }, []);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (document.getElementById('razorpay-script')) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.id = 'razorpay-script';
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const fetchPlans = async () => {
    try {
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/api/subscription/plans/`);
      const data = await response.json();
      if (data.razorpay_key_id) {
        setRazorpayKeyId(data.razorpay_key_id);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const apiUrl = getApiUrl();
      const token = localStorage.getItem(ACCESS_TOKEN);
      const response = await fetch(`${apiUrl}/api/subscription/status/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserSubscription(data);
      }
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    }
  };

  const handleSubscribe = async (planType) => {
    if (!isLoggedIn) {
      navigate('/login', { state: { from: '/subscription' } });
      return;
    }

    setLoadingPlan(planType);
    setPaymentError('');
    setPaymentSuccess('');

    try {
      const apiUrl = getApiUrl();
      const token = localStorage.getItem(ACCESS_TOKEN);
      
      // Create order
      const orderResponse = await fetch(`${apiUrl}/api/subscription/create-order/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ plan_type: planType }),
      });

      if (!orderResponse.ok) {
        const errorData = await orderResponse.json();
        throw new Error(errorData.error || 'Failed to create order');
      }

      const orderData = await orderResponse.json();
      
      // Initialize Razorpay checkout
      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'Triagz',
        description: `${planType.charAt(0).toUpperCase() + planType.slice(1)} Plan Subscription`,
        order_id: orderData.order_id,
        handler: async function (response) {
          await verifyPayment(response, planType);
        },
        prefill: {
          name: orderData.user?.name || '',
          email: orderData.user?.email || '',
        },
        notes: {
          plan_type: planType,
        },
        theme: {
          color: '#9F88FF',
        },
        modal: {
          ondismiss: function () {
            setLoadingPlan(null);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function (response) {
        setPaymentError(`Payment failed: ${response.error.description}`);
        setLoadingPlan(null);
      });
      razorpay.open();
      
    } catch (error) {
      setPaymentError(error.message);
      setLoadingPlan(null);
    }
  };

  const verifyPayment = async (razorpayResponse, planType) => {
    try {
      const apiUrl = getApiUrl();
      const token = localStorage.getItem(ACCESS_TOKEN);
      
      const verifyResponse = await fetch(`${apiUrl}/api/subscription/verify-payment/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          razorpay_order_id: razorpayResponse.razorpay_order_id,
          razorpay_payment_id: razorpayResponse.razorpay_payment_id,
          razorpay_signature: razorpayResponse.razorpay_signature,
        }),
      });

      if (!verifyResponse.ok) {
        const errorData = await verifyResponse.json();
        throw new Error(errorData.error || 'Payment verification failed');
      }

      const verifyData = await verifyResponse.json();
      
      setPaymentSuccess(`Successfully subscribed to ${planType.charAt(0).toUpperCase() + planType.slice(1)} plan!`);
      setLoadingPlan(null);
      
      // Refresh subscription status
      fetchSubscriptionStatus();
      
    } catch (error) {
      setPaymentError(error.message);
      setLoadingPlan(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setSubmitError('Please enter your email address');
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError('');
    setSubmitMessage('');
    
    try {
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/api/subscription-request/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.trim() }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSubmitMessage(data.message);
        setEmail('');
      } else {
        setSubmitError(data.error || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      setSubmitError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const plans = [
    {
      id: "free",
      name: "Starter",
      icon: <FaUsers />,
      price: "$0",
      period: "/month",
      description: "Begin your security journey with essential tools",
      features: [
        "10 credits per day",
        "1 agent profile",
        "Community support",
        "Standard response time"
      ],
      buttonText: isLoggedIn ? "Current Plan" : "Get Started",
      buttonClass: "btn-secondary",
      popular: false,
      isPurchasable: false,
      planType: "free"
    },
    {
      id: "pro",
      name: "Pro",
      icon: <FaShieldAlt />,
      price: "$30",
      period: "/month",
      description: "Advanced capabilities for serious practitioners",
      features: [
        "100 credits per day",
        "3 agent profiles",
        "Priority support",
        "Faster response time",
      ],
      buttonText: "Upgrade Now",
      buttonClass: "btn-primary",
      popular: true,
      isPurchasable: true,
      planType: "pro"
    },
    {
      id: "premium",
      name: "Premium",
      icon: <FaCrown />,
      price: "$80",
      period: "/month",
      description: "Unlimited power for demanding workflows",
      features: [
        "Unlimited credits",
        "Unlimited agent profiles",
        "24/7 priority support",
        "Fastest response time",
        "SLA guarantees"
      ],
      buttonText: "Go Premium",
      buttonClass: "btn-primary",
      popular: false,
      isPurchasable: true,
      planType: "premium"
    },
    {
      id: "enterprise",
      name: "Enterprise",
      icon: <FaBuilding />,
      price: "Custom",
      period: "",
      description: "Tailored solutions for your organization",
      features: [
        "Corporate Accounts",
        "Unlimited agent profiles",
        "Dedicated account manager",
        "Custom integrations",
        "Volume discounts"
      ],
      buttonText: "Contact Sales",
      buttonClass: "btn-secondary",
      popular: false,
      isPurchasable: false,
      isEnterprise: true,
      planType: "enterprise"
    }
  ];

  const getCurrentPlanId = () => {
    if (userSubscription?.active_subscription) {
      return userSubscription.active_subscription.plan_type;
    }
    return userSubscription?.account_tier || 'free';
  };

  const renderPlanButton = (plan) => {
    const currentPlanId = getCurrentPlanId();
    const isCurrentPlan = plan.id === currentPlanId;
    const isLoading = loadingPlan === plan.planType;
    
    if (isCurrentPlan) {
      return (
        <button className="btn btn-secondary btn-large plan-button" disabled>
          <FaCheck /> Current Plan
        </button>
      );
    }
    
    if (plan.isEnterprise) {
      return (
        <Link to="/contact" className={`btn ${plan.buttonClass} btn-large plan-button`}>
          {plan.buttonText} <FaArrowRight />
        </Link>
      );
    }
    
    if (!plan.isPurchasable) {
      if (!isLoggedIn) {
        return (
          <Link to="/register" className={`btn ${plan.buttonClass} btn-large plan-button`}>
            {plan.buttonText} <FaArrowRight />
          </Link>
        );
      }
      return (
        <button className="btn btn-secondary btn-large plan-button" disabled>
          Free Plan Active
        </button>
      );
    }
    
    return (
      <button 
        className={`btn ${plan.buttonClass} btn-large plan-button`}
        onClick={() => handleSubscribe(plan.planType)}
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <FaSpinner className="spinner" /> Processing...
          </>
        ) : (
          <>
            {plan.buttonText} <FaArrowRight />
          </>
        )}
      </button>
    );
  };

  return (
    <div className="subscription-container">
      <header className="subscription-header">
        <Logo size="small" showText={false} className="subscription-logo" />
        <nav className="nav-links">
          <Link to="/downloads" className="download-nav-link"><FaDownload /> Download</Link>
          {isLoggedIn ? (
            <Link to="/chat" className="login-link">Dashboard</Link>
          ) : (
            <>
              <Link to="/login" className="login-link">Login</Link>
              <Link to="/register" className="register-link">Register</Link>
            </>
          )}
        </nav>
      </header>

      <main className="subscription-main">
        {/* Hero Section */}
        <section className="subscription-hero">
          <div className="container">
            <div className="hero-content">
              <h1>Elevated Security Intelligence</h1>
              <p>
                Unlock the full potential of AI-powered endpoint research. Choose the plan 
                that matches your security ambitions and scale with confidence.
              </p>
            </div>
          </div>
        </section>

        {/* Payment Status Messages */}
        {(paymentError || paymentSuccess) && (
          <section className="payment-status-section">
            <div className="container">
              {paymentError && (
                <div className="payment-error">
                  <FaTimes className="error-icon" />
                  <span>{paymentError}</span>
                  <button onClick={() => setPaymentError('')} className="close-btn">
                    <FaTimes />
                  </button>
                </div>
              )}
              {paymentSuccess && (
                <div className="payment-success">
                  <FaCheck className="success-icon" />
                  <span>{paymentSuccess}</span>
                  <button onClick={() => setPaymentSuccess('')} className="close-btn">
                    <FaTimes />
                  </button>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Current Subscription Status */}
        {isLoggedIn && userSubscription?.active_subscription && (
          <section className="current-subscription-section">
            <div className="container">
              <div className="current-subscription-card">
                <h3>Your Current Subscription</h3>
                <div className="subscription-details">
                  <div className="detail-item">
                    <span className="label">Plan:</span>
                    <span className="value">{userSubscription.active_subscription.plan_type.charAt(0).toUpperCase() + userSubscription.active_subscription.plan_type.slice(1)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Status:</span>
                    <span className={`value status-${userSubscription.active_subscription.status}`}>
                      {userSubscription.active_subscription.status.charAt(0).toUpperCase() + userSubscription.active_subscription.status.slice(1)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Days Remaining:</span>
                    <span className="value">{userSubscription.active_subscription.days_remaining}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Renewal Date:</span>
                    <span className="value">
                      {new Date(userSubscription.active_subscription.end_date).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Pricing Cards Section */}
        <section className="pricing-section">
          <div className="container">
            <div className="pricing-grid">
              {plans.map((plan, index) => (
                <div key={index} className={`pricing-card ${plan.popular ? 'popular' : ''}`}>
                  {plan.popular && (
                    <div className="popular-badge">
                      <FaCrown />
                      Recommended
                    </div>
                  )}
                  
                  <div className="plan-header">
                    <div className="plan-icon">
                      {plan.icon}
                    </div>
                    <h3>{plan.name}</h3>
                    <div className="plan-price">
                      <span className="price">{plan.price}</span>
                      <span className="period">{plan.period}</span>
                    </div>
                    <p className="plan-description">{plan.description}</p>
                  </div>

                  <div className="plan-features">
                    <ul>
                      {plan.features.map((feature, featureIndex) => (
                        <li key={featureIndex}>
                          <FaCheck className="check-icon" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="plan-footer">
                    {renderPlanButton(plan)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Comparison Section */}
        <section className="comparison-section">
          <div className="container">
            <h2>Compare Plans</h2>
            <div className="comparison-table">
              <div className="comparison-header">
                <div className="feature-label">Features</div>
                <div className="plan-label">Starter</div>
                <div className="plan-label">Pro</div>
                <div className="plan-label">Premium</div>
                <div className="plan-label">Enterprise</div>
              </div>
              
              <div className="comparison-row">
                <div className="feature-name">Daily Credits</div>
                <div className="feature-value">10</div>
                <div className="feature-value">100</div>
                <div className="feature-value"><FaInfinity /></div>
                <div className="feature-value"><FaInfinity /></div>
              </div>
              
              <div className="comparison-row">
                <div className="feature-name">Agent Profiles</div>
                <div className="feature-value">1</div>
                <div className="feature-value">3</div>
                <div className="feature-value"><FaInfinity /></div>
                <div className="feature-value"><FaInfinity /></div>
              </div>
              
              <div className="comparison-row">
                <div className="feature-name">Support Level</div>
                <div className="feature-value">Community</div>
                <div className="feature-value">Priority</div>
                <div className="feature-value">24/7 Priority</div>
                <div className="feature-value">Dedicated</div>
              </div>
              
              <div className="comparison-row">
                <div className="feature-name">Response Time</div>
                <div className="feature-value">Standard</div>
                <div className="feature-value">Fast</div>
                <div className="feature-value">Fastest</div>
                <div className="feature-value">Fastest</div>
              </div>
              
              <div className="comparison-row">
                <div className="feature-name">Custom Integrations</div>
                <div className="feature-value">-</div>
                <div className="feature-value">-</div>
                <div className="feature-value">-</div>
                <div className="feature-value"><FaCheck /></div>
              </div>
              
              <div className="comparison-row">
                <div className="feature-name">SLA Guarantee</div>
                <div className="feature-value">-</div>
                <div className="feature-value">-</div>
                <div className="feature-value"><FaCheck /></div>
                <div className="feature-value"><FaCheck /></div>
              </div>
              
              <div className="comparison-row">
                <div className="feature-name">Monthly Price</div>
                <div className="feature-value">$0</div>
                <div className="feature-value">$30</div>
                <div className="feature-value">$80</div>
                <div className="feature-value">Custom</div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="faq-section">
          <div className="container">
            <h2>Frequently Asked Questions</h2>
            <div className="faq-grid">
              <div className="faq-item">
                <h3>Can I upgrade or downgrade my plan anytime?</h3>
                <p>Yes, you can change your plan at any time. When you upgrade, you'll be charged the prorated difference. When you downgrade, the change takes effect at the end of your current billing period.</p>
              </div>
              <div className="faq-item">
                <h3>What happens if I exceed my credits limit?</h3>
                <p>For Free and Pro plans, you'll need to wait until the next day or upgrade your plan. Premium users have unlimited credits.</p>
              </div>
              <div className="faq-item">
                <h3>What happens when my subscription expires?</h3>
                <p>Your account will be downgraded to the Free tier. Only your first agent profile will remain active; others will be disabled but not deleted.</p>
              </div>
              <div className="faq-item">
                <h3>Do you offer refunds?</h3>
                <p>We offer a 3 day money-back guarantee. If you're not satisfied with your subscription, contact us within 3 days of purchase for a full refund.</p>
              </div>
              <div className="faq-item">
                <h3>Is my payment information secure?</h3>
                <p>Yes, all payments are processed securely through Razorpay. We never store your card details on our servers.</p>
              </div>
              <div className="faq-item">
                <h3>Do you offer enterprise discounts?</h3>
                <p>Yes, we offer custom pricing for large organizations. Contact our sales team for a personalized quote.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Enterprise CTA Section */}
        <section className="enterprise-cta-section">
          <div className="container">
            <div className="enterprise-cta-content">
              <div className="enterprise-icon">
                <FaBuilding />
              </div>
              <h2>Need a Enterprise Subscription?</h2>
              <p>
                Our Enterprise plan offers dedicated account management, support, 
                volume discounts and custom integrations. Let's discuss 
                how triagz can meet your organization's specific needs.
              </p>
              <Link to="/contact" className="btn btn-primary btn-large">
                Contact Sales <FaArrowRight />
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="cta-section">
          <div className="container">
            <div className="cta-content">
              <h2>Begin Your Journey</h2>
              <p>
                Join elite security professionals who trust Triagz 
                to elevate their endpoint research and analysis.
              </p>
              <div className="cta-buttons">
                {isLoggedIn ? (
                  <Link to="/chat" className="btn btn-primary btn-large">
                    Go to Dashboard <FaArrowRight />
                  </Link>
                ) : (
                  <>
                    <Link to="/register" className="btn btn-primary btn-large">
                      Start Free Trial <FaArrowRight />
                    </Link>
                    <Link to="/contact" className="btn btn-secondary btn-large">
                      Contact Sales
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}

export default Subscription;
